import { Route } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { IonApp, IonContent, IonPage, IonRouterOutlet, IonSpinner, setupIonicReact, useIonToast } from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import { appConfig } from './domain/Config';

/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/react/css/padding.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/display.css';

/* Theme variables */
import './theme/variables.css';

import { PrismicProvider } from '@prismicio/react'
import { prismicClient } from './domain/Prismic'

import { initializeApp } from 'firebase/app';

import useAuth from './domain/Auth';

import GlobalContext from "./domain/GlobalContext";
import { api } from "./domain/ApiService";
import { PrismicContentType, Initiative, NestedRoute, Prompt, User } from "./domain/Types";
import { auth } from "./domain/Firebase";
import generateRoutes from './domain/Routes';
import Login from "./Login";
import React from 'react';
import InitiativePage from './pages/Initiative';
import Account from './pages/Account';
import Prompts from './pages/Prompts';
import Templates from './pages/Templates';
import Plan from './pages/Plan';
import { firebaseConfig } from './domain/Config';
import Verification from './Verification';
import { getAuth, applyActionCode, onAuthStateChanged } from 'firebase/auth';

initializeApp(firebaseConfig);

setupIonicReact({
  mode: 'md'
});

const App: React.FC = () => {
 
  const [present] = useIonToast();

  const { isAuthenticated, isLoading } = useAuth();
  let [token, setToken] = useState<string | null>(null); 
  const [user, setUser] = useState<User | null>(null);
  const [users, setUsers] = useState<User[] | null>(null);
  const [initiatives, setInitiatives] = useState<Initiative[] | null>(null);
  const getInitiative = (uid: string) => initiatives?.find(initiative => initiative.uid === uid);
  let [initiative, setInitiative] = useState<Initiative>();
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [nestedRoutes, setNestedRoutes] = useState<NestedRoute[]>([]);
  const [isDarkMode, setIsDarkMode] = useState<boolean>(true);
  const [isMenuVisible, setIsMenuVisible] = useState<boolean>(false);
  const [smallScreen, setSmallScreen] = useState<boolean>(false);
  const [showMenu, setShowMenu] = useState<boolean>(false);
  const [prismicContent, setPrismicContent] = useState<PrismicContentType>({ navigation: {} });
  const [eventTrigger, setEventTrigger] = useState(0);

  let isLoadingUserinfo = false;
  let knownRoutes = ["/", "/plan", "/account", "/prompts", "/templates"];

  useEffect(() => {
    
    if (initiative === null && !knownRoutes.includes((window as any).location.pathname)) {

      // Replace this by loading the given initiative
      (window as any).location = "/";
    }

  }, [initiative, knownRoutes]);

  useEffect(() => {

    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const actionCode = urlParams.get('oobCode');
    const actionMode = urlParams.get('mode');

    if (actionCode && actionMode === 'verifyEmail') {

      const auth = getAuth();

      const presentToast = (message: string) => {
        present({
          message: message,
          duration: 5000,
          position: "top",
        });
      };

      applyActionCode(auth, actionCode)
        .then(() => {
          onAuthStateChanged(auth, async (user) => {
            if (user) {
              (window as any).accessToken = await user.getIdToken(true);
              setToken((window as any).accessToken);
              const updatedUserInfo = await api("/userinfo", { "reset": true }, undefined);
              setUser(updatedUserInfo);
              presentToast("Email verified");
            }
          });
        })
        .catch((error) => {

          console.error(error);
          presentToast("Verification failed: "+ error);
        });
    }
  }, [present]);

  const reloadUser = async () => {
        
    if (!isLoadingUserinfo) {

      isLoadingUserinfo = true;
      const userinfo = await api("/userinfo", {}, undefined);
      userinfo.type = "Firebase";
      setUser(userinfo);
    }

    isLoadingUserinfo = false;
  };

  useEffect(() => {
  
    window.addEventListener('uerAuhenticated', reloadUser);
  
    return () => {
      window.removeEventListener('uerAuhenticated', reloadUser);
    };
  }, [reloadUser]);

  useEffect(() => {

    const fetchUsers = async () => {
      const response = await api("/user/list", {}, initiative);
      setUsers(response);
    };

    if (user)
      fetchUsers();

  }, [user, initiative]);

  useEffect(() => {
    const handleInitiativeUpdate = () => {
      setEventTrigger(prev => prev + 1);
    }; 

    window.addEventListener('initiativeUpdated', handleInitiativeUpdate);

    return () => {
      window.removeEventListener('initiativeUpdated', handleInitiativeUpdate);
    };
  }, []);

  useEffect(() => {

    let pagereload = false;
    if (!initiative) {
      pagereload = true;
    }
    if (window.location.pathname === "/") {
      pagereload = false;
    }
    initiative = initiative || JSON.parse(localStorage.getItem('initiative') as string);
    if (initiative) {
      setNestedRoutes(generateRoutes(initiative));
      if (pagereload) {
        setInitiative(initiative);
      }
      document.title = `${appConfig.appName} - ${initiative.name}`;
    }
  }, [initiative, initiative?.assistants, initiative?.folders, eventTrigger]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(user => {
      // Authentication state change logic
    });

    return unsubscribe; // Cleanup subscription on unmount
  }, [isAuthenticated]);

  const updateBodyClass = useCallback((event: MediaQueryListEvent) => {

    if (appConfig.display_mode && appConfig.display_mode === "dark") {

      document.body.classList.toggle('dark', true);
      setIsDarkMode(true);
    }
    else {

      document.body.classList.toggle('dark', event.matches);
      setIsDarkMode(event.matches);
    }    
}, []);
 
  useEffect(() => {

    if (token)
      (window as any).accessToken = token;

    if (appConfig.display_mode && appConfig.display_mode === "dark") {

      document.body.classList.toggle('dark', true);
      setIsDarkMode(true);
      return () => {};
    }
    else {

      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)');
      prefersDark.addEventListener('change', updateBodyClass);
      document.body.classList.toggle('dark', prefersDark.matches);
      return () => prefersDark.removeEventListener('change', updateBodyClass);
    }
  }, [token, updateBodyClass]);

  const listInitiatives = useCallback(async () => {
    try {

      if (user != null) {

        const response = await api("/initiative/list", {}, initiative);
        setInitiatives(response);
        window.dispatchEvent(new Event('initiativesLoaded'));
      }
  } catch (err) {
      console.error('Failed to fetch initiatives:', (err as Error).message);
    }
  }, [user, initiative]);

  useEffect(() => {
    token = token || localStorage.getItem('token');
    if (token) {
      localStorage.setItem('token', token);
      window.addEventListener('initiativeCreated', listInitiatives);
      window.addEventListener('initiativeUpdated', listInitiatives);
      window.addEventListener('initiativeRemoved', listInitiatives);
      listInitiatives();
      return () => {
        window.removeEventListener('initiativeCreated', listInitiatives);
        window.removeEventListener('initiativeUpdated', listInitiatives);
        window.removeEventListener('initiativeRemoved', listInitiatives);
      };
    }
  }, [user, listInitiatives, token]);

  const renderRoutes = (nestedRoutes: NestedRoute[], basePath: string = ''): JSX.Element[] => {
    return nestedRoutes.flatMap((route, index): JSX.Element[] => {

      const routeElement: JSX.Element = (
        <Route
          key={`${basePath}${route.path}`}
          path={`${basePath}${route.path}`}
          component={route.component}
          exact={route.exact}
        />
      );
  
      // Conditionally render child routes
      const childRoutes: JSX.Element[] = route.children 
        ? renderRoutes(route.children, `${basePath}${route.path}`)
        : [];
  
      return [routeElement, ...childRoutes];
    });
  };
  
  return (
    <IonReactRouter>
    <PrismicProvider client={prismicClient}>
      <GlobalContext.Provider value={{ 
        user, setUser, 
        token, setToken,
        users, setUsers, 
        initiatives, setInitiatives, 
        initiative, setInitiative, 
        getInitiative,
        prompts, setPrompts, 
        isDarkMode, setIsDarkMode, 
        isMenuVisible, setIsMenuVisible, 
        smallScreen, setSmallScreen, 
        showMenu, setShowMenu, 
        prismicContent, setPrismicContent 
      }}>
        <IonApp>
          {isLoading ? (
            <IonPage id="wrapper-page">
              <IonContent fullscreen>
                <div className="centered-content content-container">
                  <IonSpinner name="dots" color="dark" class="large-spinner"></IonSpinner>
                </div>
              </IonContent>
            </IonPage>
          ) : isAuthenticated ? (
            user === null ? (
              <IonPage id="wrapper-page">
                <IonContent fullscreen>                  
                  <div className="centered-content content-container">
                    <IonSpinner name="dots" color="dark" class="large-spinner"></IonSpinner>
                  </div>
                </IonContent>
              </IonPage>
            ) : !user.email_verified && appConfig.email_verification ? (
              <IonPage id="wrapper-page">
                <IonContent fullscreen>
                  <div className="centered-content content-container">
                    <Verification />
                  </div>
                </IonContent> 
              </IonPage>
            ) : (
                <IonRouterOutlet>
                  <Route exact path="/" component={InitiativePage} />
                  <Route exact path="/plan" component={Plan} />
                  <Route exact path="/account" component={Account} />
                  <Route exact path="/prompts" component={Prompts} />
                  <Route exact path="/templates" component={Templates} />
                  {renderRoutes(nestedRoutes, "/" + initiative?.name)}
                </IonRouterOutlet>
            )
          ) : (
            <IonPage id="wrapper-page">
              <IonContent fullscreen>
                <div className="centered-content content-container">
                  <Login />
                </div>
              </IonContent>
            </IonPage>
          )}
        </IonApp>
      </GlobalContext.Provider>
    </PrismicProvider>
    </IonReactRouter>
  );  
}
export default App;
